import React, { useMemo } from 'react';
import InfoTooltip from 'components/InfoTooltip/InfoTooltip';
import Points from 'components/Points';
import {
  canFundInbound,
  getCoverageShortfallLine,
  getFundingMessage,
  getNetCopy,
  OFFER_CARDS_NOT_HELD_NOTICE,
  OFFER_PRICE_LOCK_NOTICE,
} from 'utils/fundingCopy';
import { computeCoverage, computeColumnCutoff } from 'utils/offerCoverage';
import { useOfferBuilder } from '../context/OfferBuilderContext';

interface OfferCommitBarProps {
  /** Viewer's User.points (already IS available balance — escrow is an immediate debit) */
  viewerPoints: number | null;
  /** Counterparty's public points balance */
  counterpartyPoints: number | null;
  isProposing: boolean;
  onPropose: () => void;
}

/**
 * Sticky commit bar, one compact row: the value balance, the net in words
 * (prefix + pill inline, why line beneath), and the escrow outcome with
 * Propose directly under it — vertically centered, visible from first paint.
 * The price-lock and no-reservation notices live behind the (?) in the top
 * right corner (hover, keyboard focus, and tap — the copy sets trade
 * expectations and must stay discoverable). The balance bar is CARD VALUE
 * (magenta leaving, violet arriving); the net readout is POINTS — different
 * quantities, labeled as such.
 */
const OfferCommitBar: React.FC<OfferCommitBarProps> = ({
  viewerPoints,
  counterpartyPoints,
  isProposing,
  onPropose,
}) => {
  const { youSend, theySend, youSendTotal, theySendTotal, counterpartyUsername } =
    useOfferBuilder();

  const bothSidesFilled = youSend.length > 0 && theySend.length > 0;
  const combined = youSendTotal + theySendTotal;
  // Empty bar rests at even; widths transition 350ms so adds visibly move it
  const youShare = combined > 0 ? (youSendTotal / combined) * 100 : 50;
  const { netLine, whyLine, netAmount, netPrefix } = getNetCopy(youSendTotal, theySendTotal);

  const fundingKnown = viewerPoints != null && counterpartyPoints != null;

  // The same rule both columns draw their line from — computed once, in
  // offerCoverage, so the bar and the columns can never disagree.
  const coverage =
    bothSidesFilled && fundingKnown
      ? computeCoverage({
          items: theySend,
          viewerPoints,
          counterpartyPoints,
          youSendTotal,
        })
      : null;

  // Cards left unfunded across BOTH columns: each side is paid for by whoever
  // receives it, so either column can carry uncovered cards and the sentence
  // counts them all.
  const uncoveredCount = useMemo(() => {
    if (!coverage) return 0;
    const theirBudget = coverage.viewerCanOpen
      ? counterpartyPoints! + theySendTotal
      : counterpartyPoints!;
    return (
      computeColumnCutoff({ items: theySend, funderBudget: coverage.coverageBudget })
        .uncoveredIds.length +
      computeColumnCutoff({ items: youSend, funderBudget: theirBudget }).uncoveredIds.length
    );
  }, [coverage, counterpartyPoints, theySend, youSend, theySendTotal]);

  // The viewer's OWN shortfall is the gate. The counterparty's is surfaced but
  // never enforced: the viewer cannot control that balance and it may change
  // before the offer is opened.
  const viewerShortfall = coverage?.viewerShortfall ?? 0;

  /**
   * An offer that cannot complete states BOTH parties' real requirements in one
   * line. Everything else keeps its existing copy untouched.
   *
   * Each number here is that party's own requirement, not their arriving value:
   * whoever moves second is paid by the first leg and needs only the difference.
   */
  const fundingMessage = !bothSidesFilled || !fundingKnown
    ? null
    : coverage && !coverage.completable
      ? getCoverageShortfallLine({
          uncoveredCount,
          viewerShortfall: coverage.viewerShortfall,
          counterpartyShortfall: coverage.counterpartyShortfall,
        })
      : getFundingMessage({
          viewerCanFund: canFundInbound(viewerPoints, theySendTotal),
          counterpartyCanFund: canFundInbound(counterpartyPoints, youSendTotal),
          counterpartyUsername,
        });

  return (
    <footer className="offer-commit-bar" aria-label="Offer summary">
      <div className="offer-commit-balance" role="img" aria-label="Card value balance">
        <div className="offer-commit-balance-labels">
          <span className="offer-commit-balance-side offer-commit-balance-side--you">
            Leaving you · {youSendTotal.toLocaleString()} pts
          </span>
          <span className="offer-commit-balance-title">Card value</span>
          <span className="offer-commit-balance-side offer-commit-balance-side--they">
            Arriving · {theySendTotal.toLocaleString()} pts
          </span>
        </div>
        <div className={`offer-commit-balance-bar${combined === 0 ? ' is-empty' : ''}`}>
          <div className="offer-commit-balance-you" style={{ width: `${youShare}%` }} />
          <div className="offer-commit-balance-they" style={{ width: `${100 - youShare}%` }} />
          <div className="offer-commit-balance-tick">
            <span>even</span>
          </div>
        </div>
      </div>

      {/* Prefix + pill inline on one line, why line beneath. The branded pill
          must be a SIBLING of the gradient-text span: -webkit-text-fill-color:
          transparent inherits and would blank the pill's numerals if nested
          inside it. */}
      <div className="offer-commit-net">
        {netAmount != null ? (
          <span className="offer-commit-net-row">
            <span className="offer-commit-net-line offer-commit-net-line--prefix">
              {netPrefix}
            </span>
            <Points type="big" points={netAmount} />
          </span>
        ) : (
          <span className="offer-commit-net-line">{netLine}</span>
        )}
        {whyLine && <span className="offer-commit-net-why">{whyLine}</span>}
      </div>

      {/* Escrow outcome with Propose directly beneath — one block, equal in
          height to its row neighbours */}
      <div className="offer-commit-action">
        {fundingMessage && <div className="offer-commit-funding" data-testid="commit-funding">{fundingMessage}</div>}
        <button
          type="button"
          className={`offer-propose-btn${bothSidesFilled ? '' : ' offer-propose-btn--instruction'}`}
          disabled={!bothSidesFilled || isProposing || viewerShortfall > 0}
          onClick={onPropose}
        >
          {isProposing
            ? 'Proposing…'
            : bothSidesFilled
              ? `Propose trade to ${counterpartyUsername}`
              : 'Add a card to each side'}
        </button>
      </div>

      <div className="offer-commit-help">
        <InfoTooltip
          focusable
          ariaLabel="How offer pricing and card availability work"
          placement="top-end"
          text={`${OFFER_PRICE_LOCK_NOTICE} ${OFFER_CARDS_NOT_HELD_NOTICE}`}
        />
      </div>
    </footer>
  );
};

export default OfferCommitBar;
